import { useState, useEffect, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { useSamplingEvents } from "@/hooks/use-sampling-events";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useParameters } from "@/hooks/use-parameters";
import { useSamplingEventColumns } from "@/components/sampling/sampling-event-columns";
import {
  QuickFilterChips,
  type SamplingEventFilterValues,
} from "@/components/sampling/sampling-event-filters";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  Breadcrumbs,
  Combobox,
  inputClass,
} from "@/components/ui";

type SampleType = components["schemas"]["SampleType"];
type PermitType = components["schemas"]["PermitType"];

type SamplingEvent = components["schemas"]["SamplingEvent"];

interface SearchParams {
  facilityId?: string;
  is_overdue?: string;
}

export const Route = createFileRoute("/_authenticated/sampling-events/")({
  component: SamplingEventsListPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    facilityId: typeof search.facilityId === "string" ? search.facilityId : undefined,
    is_overdue: typeof search.is_overdue === "string" ? search.is_overdue : undefined,
  }),
});

const PAGE_SIZE = 20;

type QuickFilter = "all" | "scheduled" | "in_progress" | "overdue" | "completed" | "reviewed";

function SamplingEventsListPage() {
  const { t } = useTranslation(["sampling", "common", "org", "permits"]);
  const navigate = useNavigate();
  const { facilityId: urlFacilityId, is_overdue: urlOverdue } = Route.useSearch();

  const orgId = useAppStore((s) => s.activeOrgId);

  const [selectedFacilityId, setSelectedFacilityId] = useState(urlFacilityId ?? "");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [selectedParameterId, setSelectedParameterId] = useState("");
  const [parameterSearch, setParameterSearch] = useState("");
  const [filters, setFilters] = useState<SamplingEventFilterValues>(() => ({
    is_overdue: urlOverdue === "true" ? true : undefined,
  }));
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(
    urlOverdue === "true" ? "overdue" : "all",
  );
  const [offset, setOffset] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "scheduled_date", desc: true }]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as
    | "scheduled_date"
    | "collected_date"
    | "created_at"
    | "status"
    | undefined;
  const sortDir = firstSort ? (firstSort.desc ? ("desc" as const) : ("asc" as const)) : undefined;

  useEffect(() => {
    setOffset(0);
  }, [sorting]);

  const columns = useSamplingEventColumns();

  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];

  const { data: parametersData, isFetching: parametersFetching } = useParameters({
    search: parameterSearch || undefined,
    limit: 20,
  });
  const parameters = parametersData?.parameters ?? [];
  const selectedParameterLabel = selectedParameterId
    ? (parameters.find((p) => p.id === selectedParameterId)?.name ?? undefined)
    : undefined;

  const { data: selectedFacility } = useFacility(orgId, selectedFacilityId);
  const selectedFacilityLabel = selectedFacilityId
    ? (facilities.find((f) => f.id === selectedFacilityId)?.name ?? selectedFacility?.name)
    : undefined;

  const handleQuickFilterChange = useCallback((qf: QuickFilter) => {
    setQuickFilter(qf);
    setOffset(0);
    switch (qf) {
      case "all":
        setFilters((prev) => ({ ...prev, status: undefined, is_overdue: undefined }));
        break;
      case "overdue":
        setFilters((prev) => ({ ...prev, status: undefined, is_overdue: true }));
        break;
      case "scheduled":
      case "in_progress":
      case "completed":
      case "reviewed":
        setFilters((prev) => ({
          ...prev,
          status: qf as components["schemas"]["SamplingStatus"],
          is_overdue: undefined,
        }));
        break;
    }
  }, []);

  const apiParams = {
    facility_id: selectedFacilityId || undefined,
    event_type: filters.event_type,
    status: filters.status,
    date_from: filters.date_from,
    date_to: filters.date_to,
    is_overdue: filters.is_overdue,
    parameter_id: selectedParameterId || undefined,
    sample_type: filters.sample_type,
    permit_type: filters.permit_type,
    sort: sortBy,
    order: sortDir,
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, isError, error } = useSamplingEvents(orgId, apiParams);

  const events = data?.sampling_events ?? [];
  const total = data?.total ?? 0;

  const handleRowClick = (row: SamplingEvent) => {
    void navigate({
      to: "/sampling-events/$eventId",
      params: { eventId: row.id },
    });
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!orgId) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <Breadcrumbs items={[{ label: t("title") }]} />
      <PageHeader
        title={t("title")}
        subtitle={`${total} ${t("title").toLowerCase()}`}
        actions={
          <Button onClick={() => void navigate({ to: "/sampling-events/new" })}>
            {t("createTitle")}
          </Button>
        }
      />

      <div className="mt-4">
        <QuickFilterChips active={quickFilter} onChange={handleQuickFilterChange} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Combobox
          className="w-48 shrink-0"
          value={selectedFacilityId}
          onChange={(val) => {
            setSelectedFacilityId(val);
            setOffset(0);
          }}
          onSearch={setFacilitySearch}
          loading={facilitiesFetching}
          placeholder={t("filters.facility")}
          options={facilities.map((f) => ({ value: f.id, label: f.name }))}
          selectedLabel={selectedFacilityLabel}
        />
        <select
          className="w-40 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={filters.event_type ?? ""}
          onChange={(e) => {
            setFilters((prev) => ({
              ...prev,
              event_type: (e.target.value as SamplingEventFilterValues["event_type"]) || undefined,
            }));
            setOffset(0);
            setQuickFilter("all");
          }}
        >
          <option value="">{t("filters.allTypes")}</option>
          <option value="routine">{t("eventTypes.routine")}</option>
          <option value="complaint">{t("eventTypes.complaint")}</option>
          <option value="follow_up">{t("eventTypes.follow_up")}</option>
          <option value="special">{t("eventTypes.special")}</option>
          <option value="self_monitoring">{t("eventTypes.self_monitoring")}</option>
        </select>
        <input
          type="date"
          className={`${inputClass} !w-40 shrink-0`}
          value={filters.date_from ?? ""}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, date_from: e.target.value || undefined }));
            setOffset(0);
          }}
          aria-label={t("filters.dateFrom")}
        />
        <input
          type="date"
          className={`${inputClass} !w-40 shrink-0`}
          value={filters.date_to ?? ""}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, date_to: e.target.value || undefined }));
            setOffset(0);
          }}
          aria-label={t("filters.dateTo")}
        />
        <label className="flex shrink-0 items-center gap-2 text-sm text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={filters.is_overdue ?? false}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, is_overdue: e.target.checked || undefined }));
              setOffset(0);
            }}
            className="rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600"
          />
          {t("filters.isOverdue")}
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Combobox
          className="w-48 shrink-0"
          value={selectedParameterId}
          onChange={(val) => {
            setSelectedParameterId(val);
            setOffset(0);
          }}
          onSearch={setParameterSearch}
          loading={parametersFetching}
          placeholder={t("sampling:filters.parameter")}
          options={parameters.map((p) => ({ value: p.id, label: p.name }))}
          selectedLabel={selectedParameterLabel}
        />
        <select
          className="w-40 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={filters.sample_type ?? ""}
          onChange={(e) => {
            setFilters((prev) => ({
              ...prev,
              sample_type: (e.target.value as SampleType) || undefined,
            }));
            setOffset(0);
          }}
        >
          <option value="">{t("sampling:filters.sampleType")}</option>
          <option value="grab">{t("sampling:results.sampleTypes.grab")}</option>
          <option value="composite_24hr">{t("sampling:results.sampleTypes.composite_24hr")}</option>
          <option value="composite_time">{t("sampling:results.sampleTypes.composite_time")}</option>
          <option value="composite_flow">{t("sampling:results.sampleTypes.composite_flow")}</option>
          <option value="continuous">{t("sampling:results.sampleTypes.continuous")}</option>
        </select>
        <select
          className="w-44 shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          value={filters.permit_type ?? ""}
          onChange={(e) => {
            setFilters((prev) => ({
              ...prev,
              permit_type: (e.target.value as PermitType) || undefined,
            }));
            setOffset(0);
          }}
        >
          <option value="">{t("sampling:filters.permitType")}</option>
          <option value="npdes">{t("permits:types.npdes")}</option>
          <option value="pretreatment">{t("permits:types.pretreatment")}</option>
          <option value="fog">{t("permits:types.fog")}</option>
          <option value="cross_connection">{t("permits:types.cross_connection")}</option>
          <option value="stormwater_ms4">{t("permits:types.stormwater_ms4")}</option>
          <option value="stormwater_msgp">{t("permits:types.stormwater_msgp")}</option>
          <option value="sdwa">{t("permits:types.sdwa")}</option>
          <option value="state">{t("permits:types.state")}</option>
          <option value="local">{t("permits:types.local")}</option>
          <option value="caa">{t("permits:types.caa")}</option>
        </select>
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={7} rows={5} />
        </div>
      ) : events.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            title={t("listEmpty")}
            description={t("listEmptyAction")}
            actionLabel={t("createTitle")}
            onAction={() => void navigate({ to: "/sampling-events/new" })}
          />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={events}
              onRowClick={handleRowClick}
              sorting={sorting}
              onSortingChange={setSorting}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
